"use client";

/**
 * HtmlViewer — renders a raw HTML document inside a sandboxed iframe.
 *
 * HTML file-download links are fetched as text first, then supplied through
 * `srcDoc` rather than iframe `src`. This keeps the signed download URL out
 * of the embedded document and ensures the document remains sandboxed.
 */

import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

export interface HtmlViewerProps {
  /** Raw HTML text to render. */
  content: string;
  /** Filename for the iframe title. */
  fileName?: string;
  className?: string;
}

export function HtmlViewer({ content, fileName, className }: HtmlViewerProps) {
  const { t } = useTranslation();
  const externalHosts = useMemo(() => {
    const hosts = new Set<string>();
    const add = (value: string | null) => {
      if (!value) return;
      try {
        const parsed = new URL(value.startsWith("//") ? `https:${value}` : value);
        if (["http:", "https:"].includes(parsed.protocol) && /^[a-z0-9.-]{1,253}$/i.test(parsed.hostname)) hosts.add(parsed.hostname);
      } catch { /* Relative URLs are not external dependencies. */ }
    };
    // Inspect markup as text: some DOM implementations may load scripts while parsing.
    const tags = /<\s*(script|link|img)\b((?:[^>"']|"[^"]*"|'[^']*')*)>/gi;
    const attributes = /([^\s=/>]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'`=<>]+)))?/g;
    for (const tag of content.matchAll(tags)) {
      const wanted = tag[1].toLowerCase() === "link" ? "href" : "src";
      for (const attribute of tag[2].matchAll(attributes)) {
        if (attribute[1].toLowerCase() === wanted) add(attribute[2] ?? attribute[3] ?? attribute[4] ?? null);
      }
    }
    const literal = /(?:\bfetch\s*\(\s*["']|\.open\s*\(\s*["'][A-Z]+["']\s*,\s*["'])(https?:\/\/[^"'\s<>]{1,2048})["']/gi;
    for (const match of content.matchAll(literal)) add(match[1]);
    return [...hosts].slice(0, 16);
  }, [content]);

  return (
    <div
      className={cn("html-viewer flex h-full min-h-0 w-full flex-col bg-background", className)}
      data-testid="html-viewer"
    >
      {externalHosts.map((host) => <p key={host} role="alert" className="px-2 py-1 text-xs text-amber-700 dark:text-amber-300">
        {t("chat:singleFile.externalDependency", { host })}
      </p>)}
      <iframe
        srcDoc={content}
        sandbox="allow-same-origin"
        title={fileName ?? t("viewer.htmlDocument")}
        className="min-h-0 w-full flex-1 border-none"
      />
    </div>
  );
}
